import { ProfileRoles } from '@bp/pim-auth-constants';
import { LazyLoader, Select, type SelectOptionType, showToast, useDefaultSelecting } from '@bp/ui-components';
import { useQuery as useReactQuery } from '@tanstack/react-query';
import { graphql } from 'client/gql';
import type React from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';
import { useQuery } from 'urql';
import { validate } from 'uuid';
import { OrganizationConfigContext } from '../../context/OrganizationConfigContext';
import { useCreateSelectOptions } from '../../hooks/useCreateSelectOptions';
import { backendApi } from '../../utils/backendApi';
import { showErrorToast } from '../../utils/showErrorToast';
import { showSuccessToast } from '../../utils/showSuccessToast';
import { BpCard } from '../BpCard/BpCard';
import type { GrantRoleValues } from './forms/ImportPersonsForm';
import { type ProcuratPerson, useProcuratGroups } from './hooks/useProcuratGroups';
import { ImportPersonsModal } from './modals/ImportPersonsModal';
import {
  ProcuratDepartmentPersonsTable,
  type ProcuratDepartmentPersonsTableData,
} from './tables/ProcuratDepartmentPersonsTable';

const ProcuratDepartmentsPageQueryDocument = graphql(`
  query ProcuratDepartmentsPageData($organizationUuid: ID!) {
    profiles(where: { organization: { uuid: $organizationUuid } }) {
      uuid
      __typename
      foreignRefId
      updatedAt
      createdAt
      organizationRoles
    }
  }
`);

interface ImportPersonData {
  personId: number;
  role: string;
  assign?: string;
}

export const ProcuratDepartments: React.FC = () => {
  const { t } = useTranslation();
  const { organization } = useContext(OrganizationConfigContext);
  const organizationUuid = organization?.uuid;

  const { groups, isFetching: isGroupsFetching } = useProcuratGroups();
  const { rowSelection, onRowSelectionChange } = useDefaultSelecting();

  // State management
  const [importing, setImporting] = useState(false);
  const [depID, setDepID] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPersons, setSelectedPersons] = useState<ProcuratDepartmentPersonsTableData[]>([]);

  // Queries
  const [{ data: profilesData }, refetchBpPersons] = useQuery({
    query: ProcuratDepartmentsPageQueryDocument,
    variables: {
      organizationUuid: organizationUuid!,
    },
    pause: !organizationUuid,
  });

  const {
    isFetching: personsFetching,
    data: personsData,
    error: personsFetchingError,
    refetch: refetchPersons,
  } = useReactQuery<ProcuratPerson[]>({
    queryKey: ['procuratPersons', depID],
    queryFn: async () => {
      if (depID === null) return [];
      const response = await backendApi.fetch(`/procurat/groupmembers/${depID}`);
      if (!response.ok) {
        throw new Error('Failed to fetch group members');
      }
      return await response.json();
    },
    enabled: depID !== null,
  });

  // Memoized values
  const departments = useMemo(() => groups?.departments ?? [], [groups?.departments]);

  const selectData = useMemo(
    () =>
      departments.map((sy) => ({
        id: sy.id,
        label: sy.name,
      })),
    [departments],
  );

  const selectOpts = useCreateSelectOptions(selectData, 'id', 'label');

  const tableData = useMemo<ProcuratDepartmentPersonsTableData[]>(() => {
    if (!personsData) return [];

    return personsData
      .map((person) => {
        const profile = profilesData?.profiles.find((p) =>
          p.foreignRefId?.some(
            (r) => r === `procurat::${person.personId.toString()}` || r === person.personId.toString(),
          ),
        );
        const lastImport = profile?.updatedAt ?? profile?.createdAt ?? null;

        return {
          ...person,
          profile: profile?.uuid ?? 'Unknown',
          lastImport,
          imported: !!lastImport,
          roles:
            profile?.organizationRoles.filter((r) => Object.values(ProfileRoles).includes(r as ProfileRoles)) ?? [],
        };
      })
      .sort((a, b) => `${a.lastName},${a.firstName}`.localeCompare(`${b.lastName},${b.firstName}`));
  }, [personsData, profilesData]);

  // Effects
  useEffect(() => {
    if (depID !== null) {
      void refetchPersons();
    }
  }, [depID, refetchPersons]);

  // Handlers
  const handleSelectChange = useCallback(
    async (e: SingleValue<SelectOptionType>) => {
      onRowSelectionChange({});
      const newDepId = e?.value as number;
      setDepID(newDepId ?? null);

      if (personsFetchingError) {
        showErrorToast({
          graphQLErrors: [],
          name: '',
          message: personsFetchingError instanceof Error ? personsFetchingError.message : 'Unknown error',
        });
      }
    },
    [onRowSelectionChange, personsFetchingError],
  );

  const handleRoleSelect = useCallback(() => {
    setIsModalOpen(true);
    setSelectedPersons(tableData.filter((_, index) => Object.keys(rowSelection).includes(index.toString())));
  }, [rowSelection, tableData]);

  const handleImport = async (grantRoleValues: GrantRoleValues) => {
    setIsModalOpen(false);
    showToast(t('procuratImport.importStarted'), {
      type: 'info',
      theme: 'colored',
    });

    setImporting(true);

    try {
      const selectedProfiles = tableData.filter((_, index) => Object.keys(rowSelection).includes(index.toString()));
      const importData: ImportPersonData[] = selectedProfiles.map((person) => {
        const data: ImportPersonData = {
          personId: person.personId,
          role: grantRoleValues[person.personId].role,
        };

        if (validate(grantRoleValues[person.personId].assign)) {
          data.assign = grantRoleValues[person.personId].assign;
        }

        return data;
      });

      const resp = await backendApi.put(
        '/procurat/persons/import',
        JSON.stringify({
          group: depID,
          persons: importData,
        }),
      );

      if (resp.ok) {
        refetchBpPersons({ requestPolicy: 'network-only' });
        await refetchPersons();
        showSuccessToast(t('procuratImport.importSuccess'));
        onRowSelectionChange({});
      } else {
        showErrorToast({
          graphQLErrors: [],
          name: '',
          message: resp.statusText,
        });
      }
    } catch (error) {
      showErrorToast({
        graphQLErrors: [],
        name: '',
        message: error instanceof Error ? error.message : 'Import failed',
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <BpCard header={{ headline: t('procuratImport.department.plural') }} noPadding>
        {importing || isGroupsFetching ? (
          <LazyLoader embedded forceHeight='15vh' />
        ) : (
          <>
            <Select
              isSearchable
              options={selectOpts}
              placeholder={t('procuratImport.selectDepartment')}
              onChange={(e) => handleSelectChange(e as SingleValue<SelectOptionType>)}
              name='departments'
              className='px-7 mb-6'
              menuPosition='fixed'
              dense
            />

            {personsFetching ? (
              <LazyLoader embedded forceHeight='15vh' />
            ) : (
              <ProcuratDepartmentPersonsTable
                data={tableData}
                rowSelection={rowSelection}
                onRowSelectionChange={onRowSelectionChange}
                onImport={handleRoleSelect}
              />
            )}
          </>
        )}
      </BpCard>

      <ImportPersonsModal
        open={isModalOpen}
        personsData={selectedPersons}
        onImport={handleImport}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};
